import { Directional } from '../../../../components/Directional/Directional';

# Transition hirers

{/* prettier-ignore */}
<Directional /> If you have hirers using existing Ad Sync sources such as feed ingestion or web scraping,
they can be migrated to Ad Sync via the SEEK API.
This page outlines prospective approaches to gracefully migrate these hirers.
Check with us before proceeding,
as other approaches may be more appropriate to your situation.

## Feed ingestion

Hirers can migrate from JSON or XML feed ingestion to the SEEK API.

### 1. Enable interoperability

```mermaid
sequenceDiagram
participant Partner
participant Feed as Feed
participant Crawler as Crawler
participant API as SEEK API
participant Marketplace as SEEK marketplace

Partner-->>Feed: Publish

loop On configured schedule

Crawler->>Feed: Poll
Feed-->>Crawler: Positions

loop For each position
  Crawler->>Marketplace: Manage job ad

  Marketplace-->>API: Lifecycle event

  Note over Partner, API: New interoperable flow
  API-->>Partner: Lifecycle event

  Partner->>API: Retrieve job ad
  API-->>Partner: Job ad

  Partner->>Partner: Display job ad
  Partner->>Partner: Enable job ad management
end

end
```

Surface a unique identifier for each job requisition in your software’s feed,
and inform SEEK of the attribute or property path.

When a job ad is posted to our employment marketplace,
the SEEK API will emit a [`PositionProfilePosted` event] to partners with a Job Posting [relationship] with the hirer in question.
Use this event to retrieve details of the job ad, which will include:

- The unique identifier from your feed under [`seekHirerJobReference`]
- A [`seekPostingSourceCode`] of `AdSync`
- A [`seekCreatedBySelfIndicator`] of `false`

```graphql
query ($id: String!) {
  positionProfile(id: $id) {
    seekHirerJobReference
    seekCreatedBySelfIndicator

    ... on PostedPositionProfile {
      seekPostingSourceCode
    }
  }
}
```

```json label="Variables"
{
  "id": "seekAnzPublicTest:positionProfile:jobAd:2A7KS2chD"
}
```

```json label="Response"
{
  "positionProfile": {
    "seekHirerJobReference": "REQ-19999",
    "seekCreatedBySelfIndicator": false,
    "seekPostingSourceCode": "AdSync"
  }
}
```

Use [`seekHirerJobReference`] to link the existing job ad to the appropriate job requisition in your software,
following our [display guidance] to ensure the hirer understands where the job ad came from.

From here,
your software can surface regular management features on the job ad,
including the ability to edit it.

### 2. Backfill existing job ads

If your software was not already subscribed to the [`PositionProfilePosted` event],
you may [backfill] events for previously posted job ads to surface and manage them in flow.

### 3. Share positions via the SEEK API

Follow our [position syncing] guidance and start synchronising positions via the SEEK API for the hirer in question.

{/* TODO: Will deduplication handle this seamlessly, or are there race conditions to worry about? */}
{/* TODO: Can we somehow rehouse feed job ads under the newly synchronised position openings automatically in the background, or should we stick to a clean break in modelling here? */}

### 4. Turn off the feed

Notify SEEK to disable ingestion of the XML or JSON feed.

Job ads that were posted via feed ingestion will no longer receive updates.
The hirer will be able to manually edit or close these job ads in your software via the interoperable flow described above, or on the SEEK Employer website.

## Web scraping

Web scraping makes it challenging to consistently extract a unique position identifier needed for interoperability.
Hirers migrating from web scraping will have more limited options to manage existing job ads.

### 1. Share positions via the SEEK API

Follow our [position syncing] guidance and start synchronising positions via the SEEK API for the hirer in question.

When a job ad is posted to our employment marketplace,
the SEEK API will emit a [`PositionProfilePosted` event] to partners with a Job Posting [relationship] with the hirer in question.
Use this event to retrieve details of the job ad.

Use [`seekHirerJobReference`] to link the existing job ad to the appropriate job requisition in your software,
following our [display guidance] to ensure the hirer understands where the job ad came from.

From here,
your software can surface regular management features on the job ad,
including the ability to edit it.

### 2. Turn off the feed

Notify SEEK to disable ingestion via web scraping.

Job ads that were posted via web scraping will no longer receive updates.
The hirer will be able to manually edit or close these job ads on the SEEK Employer website.

[`PositionProfilePosted` event]: /use-cases/job-posting/managing-job-ads/posting-a-job-ad#positionprofileposted
[`seekCreatedBySelfIndicator`]: /schema/named-type/PostedPositionProfile#/field/seekCreatedBySelfIndicator
[`seekHirerJobReference`]: /use-cases/job-posting/identifiers-and-references#seekhirerjobreference
[`seekPostingSourceCode`]: /schema/named-type/PostedPositionProfile#/field/seekPostingSourceCode
[backfill]: /events/webhooks#replaying-a-subscriptions-events
[display guidance]: /use-cases/job-posting/ad-sync/display-job-ads#posting-sources
[relationship]: /auth/hirer-relationships
[position syncing]: /use-cases/job-posting/ad-sync/sync-positions
